<script>
  import { getContext } from 'svelte';
  import { unique_classes } from '#lib/util.js';
  import { dev } from '$app/env';
  import { BLOCKS } from '#lib/app_config.js';
  import { get_layout_orientation, get_layout, has_fullscreen_previews, has_overlapping_previews, get_default_block_spacing, get_available_tracks, get_effective_track, get_effective_track_align, supports_track_align, track_has_slack } from '#lib/StoryLayouts.js';
  import BlockGridItem from '#lib/BlockGridItem.svelte';
  import BlockSpacingHandle from '#lib/BlockSpacingHandle.svelte';
  import BlockWidthHandle from '#lib/BlockWidthHandle.svelte';
  import t from '#lib/i18n.svelte.js';
  import CanvasToolbars from '#lib/CanvasToolbars.svelte';

  let entry_api = getContext('entry_api');

  let {
    onpointerdown,
    onpointerup,
    ondblclick,
    block,
    selected,
    is_block_selection = false,
    is_editing,
    container,
    block_index,
    total_blocks,
  } = $props();
  
  // Don't show canvas tools when contextual drawer is open
  let show_canvas_tools = $derived(!entry_api?.is_disabled);

  let block_config = $derived(BLOCKS[block.type]);
  let block_element = $state(null);
  
  // Warn about unknown block types in development
  $effect(() => {
    if (!block_config && dev) {
      console.warn(`[Block] Unknown block type "${block.type}" - no component registered in BLOCKS config`);
    }
  });

  // Check if block supports spacing (has block_spacing property or is story block)
  let supports_spacing = $derived('block_spacing' in block || block.type === 'story');
  
  let is_story_block = $derived(block.type === 'story');
  let layout = $derived(is_story_block ? get_layout(block.layout) : null);
  
  // Spacing logic: symmetric until bottom is independently set
  // - block.block_spacing: controls top (and bottom when symmetric)
  // - block.block_spacing_bottom: when set, bottom is independent
  let default_spacing = $derived(is_story_block && layout ? get_default_block_spacing(layout) : 'm');
  let effective_top_spacing = $derived(block.block_spacing ?? default_spacing);
  let effective_bottom_spacing = $derived(block.block_spacing_bottom ?? block.block_spacing ?? default_spacing);
  
  /**
   * Handle top spacing change
   * In symmetric mode: sets both top and bottom
   * In independent mode: only sets top
   * @param {string} value - New spacing value
   */
  function handle_top_spacing_change(value) {
    block.block_spacing = value;
    // Note: when symmetric (block_spacing_bottom is null), bottom automatically
    // derives from block_spacing, so no need to set it explicitly
  }
  
  /**
   * Handle bottom spacing change - always sets bottom independently
   * This breaks symmetric mode if it was active
   * @param {string} value - New spacing value
   */
  function handle_bottom_spacing_change(value) {
    block.block_spacing_bottom = value;
  }

  // Width logic: only layouts whose group renders any track can be resized in place
  let available_tracks = $derived(is_story_block ? get_available_tracks(layout) : null);
  let effective_track = $derived(is_story_block ? get_effective_track(block) : null);
  let effective_track_align = $derived(is_story_block ? get_effective_track_align(block) : 'center');
  let can_align_track = $derived(is_story_block ? supports_track_align(block) : false);

  /** Why the width handles are inert, so the tooltip can say rather than just look dead */
  let width_disabled_reason = $derived(
    available_tracks ? '' : t('width_fixed_by_layout', { layout: layout?.title ?? '' })
  );

  // Measured edges of the block's content track. The block box always spans the whole page
  // grid, so both the drag handles and the selection overlay need the track's real extent
  // rather than the block's - otherwise they sit on the page margins whatever the width.
  let track_edges = $state({ left: 0, right: 0, y: 0, in_view: false });

  /** Half the handle's height, so it never hangs past the block's own edges */
  const HANDLE_HALF_HEIGHT = 44;
  /** Breathing room between the handle and the block's spacing bands */
  const HANDLE_EDGE_CLEARANCE = 16;

  $effect(() => {
    if (!is_editing || !block_element) return;

    const measure = () => {
      // Re-queried every time, not captured once: the block's own markup renders after this
      // effect first runs, so a single lookup found nothing and then never retried - which is
      // why the stripes kept spanning the whole page instead of the track.
      const track_el = block_element.querySelector('.track-anchor');
      if (!track_el) return;
      const track = track_el.getBoundingClientRect();
      const box = block_element.getBoundingClientRect();

      // Clamp to the block's *content* area, not its box. The block's spacing is drawn as
      // the striped bands top and bottom, and a handle sitting in one of those reads as
      // belonging to the gap between blocks rather than to this block.
      const layout_el = track_el.parentElement;
      const layout_box = layout_el.getBoundingClientRect();
      const layout_style = getComputedStyle(layout_el);
      const content_top = layout_box.top + parseFloat(layout_style.paddingBlockStart || 0);
      const content_bottom = layout_box.bottom - parseFloat(layout_style.paddingBlockEnd || 0);

      // Sticky, not fixed, and the two cases are distinct:
      //  - block fits on screen  -> centred on the BLOCK, full stop. Never on the viewport.
      //  - block taller than the screen -> follow the viewport centre, clamped to the block's
      //    content edges so the handles stay on the block and out of its spacing bands.
      const limit_top = content_top + HANDLE_HALF_HEIGHT + HANDLE_EDGE_CLEARANCE;
      const limit_bottom = content_bottom - HANDLE_HALF_HEIGHT - HANDLE_EDGE_CLEARANCE;
      const content_centre = (content_top + content_bottom) / 2;
      const fits_on_screen = (content_bottom - content_top) <= window.innerHeight;
      const y = (fits_on_screen || limit_bottom < limit_top)
        ? content_centre
        : Math.min(Math.max(window.innerHeight / 2, limit_top), limit_bottom);
      const in_view = box.bottom > 0 && box.top < window.innerHeight;

      // Centred in the striped margin band, exactly like BlockSpacingHandle sits centred in
      // the striped spacing band above and below. The band runs from the block's edge to the
      // track's edge; sitting ON the track edge is what kept looking wrong.
      track_edges = {
        left: (box.left + track.left) / 2,
        right: (track.right + box.right) / 2,
        y,
        in_view,
      };

      // The selection overlay masks its stripes to the block box, which spans the whole page
      // grid, so without these it stripes the full page width whatever the block's width.
      const inset_start = `${Math.max(0, Math.round(track.left - box.left))}px`;
      const inset_end = `${Math.max(0, Math.round(box.right - track.right))}px`;
      if (block_element.style.getPropertyValue('--track-inset-start') !== inset_start) {
        block_element.style.setProperty('--track-inset-start', inset_start);
      }
      if (block_element.style.getPropertyValue('--track-inset-end') !== inset_end) {
        block_element.style.setProperty('--track-inset-end', inset_end);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(block_element);
    // Picks up the track anchor once the block's markup lands, and any later layout change
    const mutations = new MutationObserver(measure);
    mutations.observe(block_element, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  });

  /**
   * Handle width change from either edge handle.
   * Dragging an edge outwards widens the block, so it also re-centres a block that was
   * pushed to one side - otherwise the far edge would appear not to move.
   * @param {string} value - New track value
   */
  function handle_track_change(value) {
    block.track = value;
    if (!track_has_slack(value)) block.track_align = null;
  }
  let layout_orientation = $derived(layout ? get_layout_orientation(layout) : 'vertical');
  let is_fullscreen_layout = $derived(layout ? has_fullscreen_previews(layout) : false);
  let is_overlapping_layout = $derived(layout ? has_overlapping_previews(layout) : false);
  
  // Track story block ref state for embed toolbar
  // Note: block.ref is bound to StoryBlock's ref prop via bind:ref
  let story_ref = $derived(is_story_block ? block.ref : null);
  let hovered_preview_index = $derived(story_ref?.hovered_preview_index ?? null);
  
  // Track if embed toolbar is being hovered or menu is open (to lock position)
  let embed_toolbar_hovered = $state(false);
  let embed_menu_is_open = $state(false);
  
  // When toolbar is "active" (hovered or menu open), lock to current card
  let toolbar_is_active = $derived(embed_toolbar_hovered || embed_menu_is_open);
  
  // Remember the anchor/index when toolbar becomes active
  let locked_embed_anchor = $state(null);
  let locked_embed_index = $state(0);
  
  $effect(() => {
    const anchor = story_ref?.active_preview_anchor;
    if (!anchor) return;
    
    // Only update when toolbar is not active or when establishing initial lock
    if (toolbar_is_active && locked_embed_anchor !== null) return;
    
    locked_embed_anchor = anchor;
    if (hovered_preview_index !== null) {
      locked_embed_index = hovered_preview_index;
    }
  });
  
  // Use locked values when toolbar is active, otherwise follow hover
  let embed_anchor = $derived(
    toolbar_is_active 
      ? locked_embed_anchor 
      : (story_ref?.active_preview_anchor ?? null)
  );
  let embed_index = $derived(
    toolbar_is_active 
      ? locked_embed_index 
      : (hovered_preview_index ?? locked_embed_index)
  );
  let embed_total = $derived(block.entry_previews?.length ?? 0);

  /* Every preview card of the selected block carries its own "more actions" button, rather
     than one button that materialises on the card under the cursor. Hover-only made the menu
     undiscoverable (two invisible gates: select the block, then hover a card) and left it
     unreachable on touch, where mouseenter never fires. Overlapping/tilted layouts are the
     exception - there a per-card button would sit on the neighbouring card, so they keep the
     single toolbar that follows the hovered card. */
  let persistent_embed_tools = $derived(!is_overlapping_layout);

  let embed_slots = $derived.by(() => {
    const previews = block.entry_previews ?? [];
    if (!is_story_block || previews.length === 0) return [];

    if (persistent_embed_tools) {
      const anchors = story_ref?.preview_anchors ?? [];
      return previews
        .map((preview, index) => ({
          index,
          anchor: anchors[index],
          visibility: preview?.visibility ?? 'public',
        }))
        .filter(slot => slot.anchor);
    }

    if (!embed_anchor) return [];
    return [{
      index: embed_index,
      anchor: embed_anchor,
      visibility: previews[embed_index]?.visibility ?? 'public',
    }];
  });

  // For multi-selection, toolbar shows on the hovered block (or anchor if none hovered)
  // This allows the user to interact with any selected block's toolbar by hovering it
  let is_toolbar_anchor = $derived(() => {
    if (!is_block_selection || !selected) return false;
    // Use container's toolbar_block_index which handles hover logic
    return container?.toolbar_block_index?.() === block_index;
  });
  
  // Track hover state for multi-selection toolbar positioning
  function handle_pointer_enter() {
    if (is_editing && selected && is_block_selection) {
      container.hovered_selected_index = block_index;
    }
  }
  
  function handle_pointer_leave() {
    // Only clear if we're still the hovered block (prevents race conditions)
    if (container?.hovered_selected_index === block_index) {
      container.hovered_selected_index = null;
    }
  }
  
  let anchor_name = $derived(
    is_editing && is_toolbar_anchor() ? '--selected-block' : null
  );

  let class_names = $derived(
    unique_classes(
      `${block.type}-block`,
      block.type === 'embed' && `display-${block.display.type}`,
      is_editing && 'is_editing',
      // add block colors
      block?.color && block?.color.replace(/_/g, '-'),
      block?.color && `has-color-set`,
    )
  );
  
  // Embed action callbacks (delegate to StoryBlock via ref), addressed by card index.
  // The toolbar passes the target index; move_entry_preview clamps out-of-range moves.
  function handle_embed_move(index, target) {
    block.ref?.move_entry_preview?.(index, target);
  }
  
  function handle_embed_edit(index) {
    const entry_id = block.entry_previews?.[index]?.entry_id;
    if (entry_id) {
      entry_api?.start_contextual_editing?.(entry_id);
    }
  }
  
  function handle_embed_remove(index) {
    block.ref?.remove_entry_preview?.(index);
  }
</script>

<!-- 
  This will be called from <Container> and wraps a block component. A design goal is to
  make blocks as independent as possible, so that the implementer of a new block node does
  not need to study our system.
-->
{#if block_config?.component}
<BlockGridItem
  {onpointerup}
  {onpointerdown}
  {ondblclick}
  onpointerenter={handle_pointer_enter}
  onpointerleave={handle_pointer_leave}
  id={block.block_id}
  class='{class_names} {selected ? 'block-selected' : ''}'
  tracks={block?.tracks}
  color={block?.color}
  observe_inview={true}
  observe_inview_once={false}
  {anchor_name}
  bind:element={block_element}
>
  {@const BlockTypeComponent = block_config?.component}
  <BlockTypeComponent
    bind:ref={block.ref}
    {block}
    {is_editing}
    {selected}
  />
</BlockGridItem>
{/if}

<!-- Spacing drag handles (rendered in top-layer via popover) -->
<!-- Top handle: in symmetric mode, controls both; in independent mode, only top -->
<!-- Bottom handle: always sets bottom independently, breaking symmetric mode -->
{#if show_canvas_tools && supports_spacing && is_editing && selected && is_block_selection && block_element}
  <BlockSpacingHandle 
    anchor={block_element}
    spacing={effective_top_spacing}
    position="top"
    onchange={handle_top_spacing_change}
  />
  <BlockSpacingHandle
    anchor={block_element}
    spacing={effective_bottom_spacing}
    position="bottom"
    onchange={handle_bottom_spacing_change}
  />
{/if}

<!-- Width drag handles on the left/right margins. Rendered for every selected story block,
     disabled where the layout's width is not a free variable, so the affordance is always
     discoverable and its absence never reads as a broken feature. -->
{#if show_canvas_tools && is_editing && selected && is_block_selection && block_element && is_story_block && track_edges.in_view}
  {#each ['left', 'right'] as edge}
    <BlockWidthHandle
      anchor={block_element}
      track={effective_track}
      tracks={available_tracks ?? []}
      position={edge}
      edge_x={edge === 'left' ? track_edges.left : track_edges.right}
      edge_y={track_edges.y}
      disabled={!available_tracks}
      disabled_reason={width_disabled_reason}
      onchange={handle_track_change}
    />
  {/each}
{/if}

<!-- Unified canvas toolbar (block tools + embed tools when preview is hovered) -->
<!-- Only shown for the anchor block when multiple blocks are selected -->
{#if show_canvas_tools && is_editing && is_toolbar_anchor() && block_element && container}
  <CanvasToolbars 
    block_anchor={block_element}
    {block_index}
    {total_blocks}
    onmove={(direction) => container.move_block(direction)}
    {embed_slots}
    {embed_total}
    embed_tools_persistent={persistent_embed_tools}
    embed_hovered_index={hovered_preview_index}
    embed_orientation={layout_orientation}
    {is_fullscreen_layout}
    on_embed_move={handle_embed_move}
    on_embed_edit={handle_embed_edit}
    on_embed_remove={handle_embed_remove}
    on_embed_toolbar_hover={(hovering) => embed_toolbar_hovered = hovering}
    on_embed_menu_open={(open) => embed_menu_is_open = open}
  />
{/if}
